/**
 * Centralized Notification Security Configuration
 * Defines which roles can see which notification types
 */

// Notification permission configuration
export const NOTIFICATION_PERMISSIONS = {
  WITHDRAWALS: {
    type: 'withdrawals',
    allowedRoles: ['admin', 'finance'],
    dataPath: 'withdrawdata.withdraws',
    filterField: 'processed',
    filterValue: false,
    route: '/withdraws',
    icon: 'MoneyIcon',
    iconColor: '#ff9800',
    translationKey: 'withdraws_web',
    countKey: 'pending_withdrawals'
  },
  SOS: {
    type: 'sos',
    allowedRoles: ['admin', 'callcenteragent', 'callcentersupervisor'],
    dataPath: 'sosdata.sos',
    filterField: 'processed',
    filterValue: false,
    route: '/sos',
    icon: 'SosIcon',
    iconColor: '#f44336',
    translationKey: 'sos',
    countKey: 'pending_sos'
  },
  COMPLAINTS: {
    type: 'complaints',
    allowedRoles: ['admin', 'callcentersupervisor'],
    dataPath: 'complaindata.list',
    filterField: 'check',
    filterValue: false,
    route: '/complain',
    icon: 'AnnouncementOutlinedIcon',
    iconColor: '#2196f3',
    translationKey: 'complain',
    countKey: 'pending_complaints'
  }
};

/**
 * Check if a user role has permission to see a notification type
 * @param {string} notificationType - The type of notification
 * @param {string} userRole - The user's role
 * @returns {boolean} True if user can see the notification
 */
export const canSeeNotification = (notificationType, userRole) => {
  if (!userRole || !notificationType) return false;
  
  const config = Object.values(NOTIFICATION_PERMISSIONS).find(
    n => n.type === notificationType
  );
  
  if (!config) return false;
  
  return config.allowedRoles.includes(userRole);
};

/**
 * Get all notification types a user can see
 * @param {string} userRole - The user's role
 * @returns {Array} Array of notification configurations user can see
 */
export const getUserNotifications = (userRole) => {
  if (!userRole) return [];
  
  return Object.values(NOTIFICATION_PERMISSIONS).filter(
    notification => notification.allowedRoles.includes(userRole)
  );
};

/**
 * Calculate notification count for a specific type
 * @param {string} notificationType - The type of notification
 * @param {Object} data - The data object containing notifications
 * @param {string} userRole - The user's role
 * @returns {number} Count of unprocessed notifications
 */
export const getNotificationCount = (notificationType, data, userRole) => {
  // First check if user has permission
  if (!canSeeNotification(notificationType, userRole)) {
    return 0;
  }
  
  const config = Object.values(NOTIFICATION_PERMISSIONS).find(
    n => n.type === notificationType
  );
  
  if (!config || !data) return 0;
  
  // Get the data array based on the configuration
  let dataArray = null;
  if (notificationType === 'withdrawals' && data.withdraws) {
    dataArray = data.withdraws;
  } else if (notificationType === 'sos' && data.sos) {
    dataArray = data.sos;
  } else if (notificationType === 'complaints' && data.list) {
    dataArray = data.list;
  }
  
  if (!dataArray || !Array.isArray(dataArray)) return 0;
  
  // Filter based on the configuration
  // Special handling for withdrawals - exclude cancelled ones
  if (notificationType === 'withdrawals') {
    return dataArray.filter(item => 
      item[config.filterField] === config.filterValue && !item.reversed
    ).length;
  }
  
  // Default filtering for other notification types
  return dataArray.filter(item => 
    item[config.filterField] === config.filterValue
  ).length;
};

/**
 * Get total notification count for a user
 * @param {Object} withdrawData - Withdrawal data
 * @param {Object} sosData - SOS data
 * @param {Object} complainData - Complaint data
 * @param {string} userRole - The user's role
 * @returns {number} Total count of notifications user can see
 */
export const getTotalNotificationCount = (withdrawData, sosData, complainData, userRole) => {
  if (!userRole) return 0;
  
  let total = 0;
  
  // Add withdrawals if user has permission
  if (canSeeNotification('withdrawals', userRole)) {
    total += getNotificationCount('withdrawals', withdrawData, userRole);
  }
  
  // Add SOS if user has permission
  if (canSeeNotification('sos', userRole)) {
    total += getNotificationCount('sos', sosData, userRole);
  }
  
  // Add complaints if user has permission
  if (canSeeNotification('complaints', userRole)) {
    total += getNotificationCount('complaints', complainData, userRole);
  }
  
  return total;
};

/**
 * Security audit log for notification access
 * @param {string} userRole - The user's role
 * @param {string} notificationType - The notification type accessed
 * @param {boolean} granted - Whether access was granted
 */
export const logNotificationAccess = (userRole, notificationType, granted) => {
  const timestamp = new Date().toISOString();
  const logEntry = {
    timestamp,
    userRole,
    notificationType,
    granted,
    message: granted 
      ? `User with role '${userRole}' accessed ${notificationType} notifications`
      : `User with role '${userRole}' was denied access to ${notificationType} notifications`
  };
  
  if (!granted) {
    console.warn('Notification Access Denied:', logEntry);
  } else {
    console.log('Notification Access:', logEntry);
  }
  
  return logEntry;
};

/**
 * Get notification badge color based on count and type
 * @param {string} notificationType - The type of notification
 * @param {number} count - The notification count
 * @returns {string} Badge color
 */
export const getNotificationBadgeColor = (notificationType, count) => {
  if (count === 0) return 'default';
  
  // Critical notifications
  if (notificationType === 'sos') return 'error';
  
  // Financial notifications
  if (notificationType === 'withdrawals') return 'warning';
  
  // Default for other types
  return 'primary';
};

/**
 * Validate notification data integrity
 * @param {Object} data - The notification data
 * @param {string} notificationType - The type of notification
 * @returns {boolean} True if data is valid
 */
export const validateNotificationData = (data, notificationType) => {
  const config = Object.values(NOTIFICATION_PERMISSIONS).find(
    n => n.type === notificationType
  );
  
  if (!config) return false;
  
  // Check if the expected data structure exists
  if (notificationType === 'withdrawals') {
    return data && Array.isArray(data.withdraws);
  } else if (notificationType === 'sos') {
    return data && Array.isArray(data.sos);
  } else if (notificationType === 'complaints') {
    return data && Array.isArray(data.list);
  }
  
  return false;
};

/* ------------------------------------------------------------------ *
 * Push-notification management capability
 *
 * Sending / scheduling push notifications is an admin power by role.
 * Instead of cloning a whole role for one extra page, an admin can grant
 * the capability to an individual staff member by setting the boolean
 * `canManageNotifications` on their /users/<uid> record (Internal Staff
 * tab). The flag is additive: the person keeps whatever their usertype
 * already allows, and the Realtime Database rules honour the same flag,
 * so it is real access control and not just a hidden button.
 * ------------------------------------------------------------------ */

// Field name on the user profile. Mirrored in json/database-rules.json.
export const NOTIFICATION_CAPABILITY = 'canManageNotifications';

// Roles that manage push notifications purely by virtue of their role.
export const NOTIFICATION_MANAGER_ROLES = ['admin'];

/**
 * Can this profile view, create, schedule and delete push notifications?
 * @param {Object} profile - auth.profile
 * @returns {boolean}
 */
export const canManagePushNotifications = (profile) => {
  if (!profile) return false;
  if (NOTIFICATION_MANAGER_ROLES.includes(profile.usertype)) return true;
  return profile[NOTIFICATION_CAPABILITY] === true;
};
